import { useTime } from "./core.js";

/**
 * Full-screen overlays for the round outcome:
 *   VaultDoorSlam — heavy door drops from top of frame on bust.
 *   AlarmOverlay  — red strobing edges + door slam + ALARM TRIPPED title + 0.00× delta.
 *   RewardCard    — vault door rises, illuminated chest, payout multiplier + delta.
 */

// ---- VaultDoorSlam --------------------------------------------------------
export function VaultDoorSlam({ progress = 1 }: { progress?: number }) {
  const y = -100 + Math.min(progress / 0.4, 1) * 100;
  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        height: "26%",
        transform: `translateY(${y - 100}%)`,
        background: `
          linear-gradient(180deg, #1A1F2E 0%, #0E1220 60%, #050810 100%),
          repeating-linear-gradient(0deg, rgba(196,154,70,0.05) 0px, rgba(196,154,70,0.05) 2px, transparent 2px, transparent 8px)
        `,
        borderBottom: "3px solid var(--gold-deep)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.7), 0 0 40px rgba(224,69,69,0.4)",
        zIndex: 50,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 14,
          display: "flex",
          justifyContent: "space-around",
          padding: "0 24px",
        }}
      >
        {Array.from({ length: 11 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "radial-gradient(circle at 30% 30%, var(--gold-bright), var(--gold-deep))",
              border: "1px solid var(--gold-shadow)",
            }}
          />
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "30%",
          transform: "translateX(-50%)",
          fontFamily: "var(--font-display)",
          fontSize: 22,
          letterSpacing: 6,
          color: "var(--red-bright)",
          textShadow: "0 0 12px var(--red), 0 2px 0 #000",
        }}
      >
        SEALED
      </div>
    </div>
  );
}

// ---- AlarmOverlay ---------------------------------------------------------
export function AlarmOverlay({
  visible = true,
  mineCount = 5,
  bet = 0.001,
  currency = "BTC",
}: {
  visible?: boolean;
  multiplier?: number;
  mineCount?: number;
  bet?: number;
  currency?: string;
}) {
  const t = useTime();
  if (!visible) return null;
  const strobe = Math.sin(t * 12) > 0.6 ? 1 : 0.3;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 40,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          boxShadow: `inset 0 0 ${60 + strobe * 60}px rgba(224,69,69,${0.4 + strobe * 0.4})`,
          opacity: strobe,
          transition: "opacity 80ms",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse at 50% 100%, rgba(224,69,69,0.18), transparent 60%)",
        }}
      />
      <VaultDoorSlam progress={1} />

      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "44%",
          textAlign: "center",
          pointerEvents: "auto",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 18px",
            background: "rgba(224,69,69,0.15)",
            border: "1px solid rgba(224,69,69,0.5)",
            borderRadius: 3,
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 4,
            color: "var(--red-bright)",
            textTransform: "uppercase",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "var(--red-bright)",
              boxShadow: "0 0 10px var(--red-bright)",
              opacity: strobe,
            }}
          />
          Alarm tripped
        </div>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 96,
            color: "var(--red-bright)",
            letterSpacing: -2,
            lineHeight: 0.9,
            marginTop: 12,
            textShadow: "0 0 20px var(--red), 0 0 40px var(--red-deep)",
          }}
        >
          0.00<span style={{ fontSize: 56 }}>×</span>
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            color: "var(--fg-2)",
            marginTop: 16,
          }}
        >
          −{bet.toFixed(8)} {currency}
          <span style={{ color: "var(--fg-4)" }}>
            {" "}· {mineCount} {mineCount === 1 ? "mine" : "mines"} on the floor
          </span>
        </div>
      </div>
    </div>
  );
}

// ---- RewardCard -----------------------------------------------------------
export function RewardCard({
  visible = true,
  multiplier = 2.34,
  payout = 0.00234,
  currency = "BTC",
  mineCount = 5,
  picks = 3,
}: {
  visible?: boolean;
  multiplier?: number;
  payout?: number;
  currency?: string;
  mineCount?: number;
  picks?: number;
}) {
  const t = useTime();
  if (!visible) return null;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 40,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          height: "26%",
          transform: "translateY(-100%)",
          background: "linear-gradient(180deg, #1A1F2E 0%, #0E1220 60%, #050810 100%)",
          borderBottom: "3px solid var(--gold-deep)",
          opacity: 0.4,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          height: "60%",
          background:
            "radial-gradient(ellipse 50% 100% at 50% 0%, rgba(255,232,168,0.30) 0%, rgba(241,203,107,0.15) 30%, transparent 60%)",
          mixBlendMode: "screen",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "44%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          pointerEvents: "auto",
        }}
      >
        <svg
          width="120"
          height="80"
          viewBox="-60 -40 120 80"
          style={{
            filter: "drop-shadow(0 0 24px var(--gold-bright)) drop-shadow(0 0 12px var(--gold))",
          }}
        >
          <defs>
            <linearGradient id="rc-body" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#3A2D14" />
              <stop offset="0.5" stopColor="#241B0C" />
              <stop offset="1" stopColor="#13100A" />
            </linearGradient>
            <linearGradient id="rc-band" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--gold-bright)" />
              <stop offset="1" stopColor="var(--gold-deep)" />
            </linearGradient>
            <radialGradient id="rc-glow" cx="0.5" cy="0.4" r="0.6">
              <stop offset="0" stopColor="var(--gold-glow)" stopOpacity="0.9" />
              <stop offset="1" stopColor="var(--gold-bright)" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect x="-50" y="-10" width="100" height="40" fill="url(#rc-body)" stroke="var(--gold-deep)" strokeWidth="0.6" />
          <path
            d="M-50,-10 Q-50,-40 0,-40 Q50,-40 50,-10 Z"
            fill="url(#rc-body)"
            stroke="var(--gold-deep)"
            strokeWidth="0.6"
          />
          <ellipse cx="0" cy="-12" rx="40" ry="18" fill="url(#rc-glow)" opacity="0.9" />
          <ellipse cx="0" cy="-12" rx="42" ry="6" fill="var(--gold-glow)" opacity="0.7" />
          <rect x="-50" y="-2" width="100" height="3" fill="url(#rc-band)" />
          <rect x="-50" y="20" width="100" height="3" fill="url(#rc-band)" />
          <rect x="-6" y="-2" width="12" height="14" fill="url(#rc-band)" stroke="var(--gold-shadow)" strokeWidth="0.4" />
          <circle cx="0" cy="6" r="2" fill="var(--gold-shadow)" />
        </svg>

        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 144,
            color: "var(--gold-bright)",
            letterSpacing: -4,
            lineHeight: 0.85,
            marginTop: 8,
            textShadow: "0 0 24px var(--gold), 0 0 48px var(--gold-deep)",
          }}
        >
          {multiplier.toFixed(2)}
          <span style={{ fontSize: 80 }}>×</span>
        </div>

        <div
          style={{
            display: "inline-flex",
            gap: 12,
            alignItems: "baseline",
            padding: "6px 16px",
            marginTop: 10,
            background: "rgba(91,208,138,0.12)",
            border: "1px solid rgba(91,208,138,0.4)",
            borderRadius: 3,
            fontFamily: "var(--font-mono)",
            fontSize: 18,
            color: "var(--green-bright)",
            fontWeight: 600,
          }}
        >
          +{payout.toFixed(8)} {currency}
          <span style={{ fontSize: 11, color: "var(--green)", fontWeight: 500 }}>
            ≈ ${(payout * 65000).toFixed(2)}
          </span>
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--fg-3)",
            marginTop: 12,
            letterSpacing: 0.5,
          }}
        >
          {picks} safe {picks === 1 ? "pick" : "picks"} · {mineCount}{" "}
          {mineCount === 1 ? "mine" : "mines"} avoided · seed reveals on resolve
        </div>
      </div>

      {/* Particle accent — sparse gold motes rising. Single-cycle: after 2.5s
          the burst is over, so we don't paint forever after the user has seen it. */}
      {t < 2.5 && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {Array.from({ length: 14 }).map((_, i) => {
            const seed = i * 37;
            // Single-shot rise from y=100% to y=20% over the 2.5s window.
            const ph = Math.min(1, (t * 0.4 + seed * 0.04));
            const x = 20 + ((seed * 13) % 60);
            const y = 100 - ph * 80;
            const op = Math.max(0, (1 - ph) * 0.8);
            if (op < 0.02) return null;
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: `${x}%`,
                  top: `${y}%`,
                  width: 3,
                  height: 3,
                  borderRadius: "50%",
                  background: "var(--gold-bright)",
                  opacity: op,
                  boxShadow: "0 0 6px var(--gold-bright)",
                  transform: `translate(-50%, -50%) scale(${1 + Math.sin(t * 2 + i) * 0.2})`,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
